import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "/logo.png";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#110A06] flex items-center justify-center px-4 relative">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-[#D4A54A]/5 blur-[120px]" />
      <div className="w-full max-w-md relative z-10" data-testid="login-page">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img src={LOGO_URL} alt="KarmaCircle" className="w-16 h-16 rounded-full object-cover ring-2 ring-[#D4A54A]/30 mx-auto" />
          </Link>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#F5E6C8]">Welcome Back</h1>
          <p className="font-sans text-sm text-[#A89070] mt-2">Continue your karma journey</p>
        </div>

        <div className="bg-[#1A1008] rounded-3xl border border-[#3D2D1D]/50 p-8">
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 border border-[#3D2D1D] rounded-full px-6 py-3 font-sans text-sm font-medium text-[#F5E6C8] hover:border-[#D4A54A]/50 hover:bg-[#23180E] transition-all duration-200 mb-6"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#3D2D1D]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#1A1008] px-4 text-[#A89070] font-sans">or sign in with email</span></div>
          </div>

          {error && (
            <div data-testid="login-error" className="bg-[#C0392B]/10 border border-[#C0392B]/20 text-[#E07A5F] rounded-xl px-4 py-3 mb-4 font-sans text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5">Email</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email-input"
                className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                  data-testid="login-password-input"
                  className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 pr-10 focus:border-[#D4A54A] placeholder:text-[#5C4830]"
                  placeholder="Enter your password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89070] hover:text-[#D4A54A]">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-8 py-3 font-sans font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20 disabled:opacity-60 disabled:translate-y-0"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center mt-6 font-sans text-sm text-[#A89070]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#D4A54A] font-medium hover:underline" data-testid="register-link">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
